// ---- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { useUpdateEngineData } from "../../../hooks/useUpdateEngineData";

/**
 * Custom hook for UpdateEngine grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the jobs grid
 * - Fetches sorted jobs data from the API via useUpdateEngineData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useUpdateEngineGridUtils({ columnHandlers = [] } = {}) {
  const { fetchJobs, updateUpdateEngine, updateEngineRange } =
    useUpdateEngineData();
  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update jobs
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchJobs(updateEngineRange, null, customSort);

      if (data) {
        updateUpdateEngine({
          jobs: data?.content,
          totalPages: data?.totalPages,
          totalItems: data?.totalElements,
          currentPage: data?.pageable?.pageNumber,
          updateEngineSort: customSort,
        });
      }
    },
    [fetchJobs, updateUpdateEngine, updateEngineRange],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["updateEngine"](...columnHandlers)["default"];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
